// Typed localStorage preference helpers. One module so storage keys never
// scatter: every reader preference (target language, font sizes, sync mode,
// and — phase 5 — local bookmarks) is read and written through here, under a
// single versioned key prefix.

import type { TargetLang } from "@bindery/convex/model/language";
import type {
  FontSizePrefs,
  LocalBookmark,
  LocalReadMark,
  SyncMode,
} from "@bindery/shared/corpus/reader";

/** Versioned prefix for every key this app owns in localStorage. */
const PREFIX = "retells:";

/**
 * Stable key names (unprefixed). Values are serialized as JSON. Add new
 * preferences here so the set of keys stays discoverable in one place.
 */
export const PREF_KEYS = {
  /** Reader's global target language for gloss/translation. */
  targetLang: "targetLang",
  /** Per-pane font-size preference (`FontSizePrefs`). */
  fontSize: "fontSize",
  /** Sync alignment mode toggle (`SyncMode`). */
  syncMode: "syncMode",
  /** Locally stored bookmarks (phase 5). */
  bookmarks: "bookmarks",
  /** Volumes the reader has marked finished (`src/reader/read-marks.ts`). */
  readMarks: "readMarks",
  /** Anonymous, stable per-browser correlation id (phase 9 logging). */
  sessionId: "sessionId",
  /**
   * Marketing lead id — handed over by the marketing site's `visitor-id` link
   * param, or minted here on a direct arrival
   * (`src/lib/attribution.ts`).
   */
  leadId: "leadId",
  /**
   * LAST-TOUCH ad click id (`StoredClickId`), so the click that paid for a
   * purchase is still known at checkout — weeks after the landing that carried
   * it, and long after the `acquisition.landed` event has aged out of Axiom.
   */
  clickId: "clickId",
  /** Epoch ms until which the founder's over-limit notice stays silenced
   *  after they dismiss it. */
  founderNoticeSilencedUntil: "founderNoticeSilencedUntil",
  /**
   * What this browser has observed about its own engagement, and the highest
   * tier the server has acknowledged (`src/lib/engagement/`).
   *
   * LOCAL BY DESIGN. Routes browsed and units finished are watched here for
   * free, and Convex is touched only when the local picture says a threshold
   * has just been crossed — a handful of calls per visitor lifetime rather
   * than anything resembling the ten-second reading flush.
   */
  engagement: "engagement",
  /**
   * Epoch ms when the one-time ad-measurement disclosure toast was shown
   * (`src/components/ad-privacy-notice.tsx`). Set means never show it again.
   */
  privacyNoticeShownAt: "privacyNoticeShownAt",
} as const;

export type PrefKey = (typeof PREF_KEYS)[keyof typeof PREF_KEYS];

/**
 * One ad-platform click id, with the param it arrived under and when.
 *
 * Stored as ONE object rather than three keys because the three are only
 * meaningful together: a `gclid` value filed under `msclkid` would be uploaded
 * to the wrong platform, and a timestamp from a different landing would put
 * the conversion outside its click's upload window. Written and read only by
 * `src/lib/attribution.ts`.
 */
export interface StoredClickId {
  /** The campaign param it came in on — `"gclid"`, `"msclkid"`, … */
  source: string;
  /** The opaque click id itself. */
  value: string;
  /** Epoch ms of the landing that carried it — our proxy for the click time. */
  at: number;
}

/**
 * The stored value type for each key, so `getPref`/`setPref` are checked
 * against the right shape instead of `getPref` inferring solely from the
 * caller's fallback (which let e.g. a `FontSizePrefs` object slip in under
 * `syncMode` unnoticed). Phase 5 defines `bookmarks` as a `LocalBookmark[]`.
 */
export interface PrefValueMap {
  targetLang: TargetLang;
  fontSize: FontSizePrefs;
  syncMode: SyncMode;
  bookmarks: LocalBookmark[];
  readMarks: LocalReadMark[];
  sessionId: string;
  leadId: string;
  clickId: StoredClickId;
  founderNoticeSilencedUntil: number;
  engagement: StoredEngagement;
  privacyNoticeShownAt: number;
}

/**
 * This browser's own record of what it has seen, and how far the server has
 * agreed.
 *
 * `reportedTier` is the SERVER's answer, not the local guess — the mutation
 * returns what it actually stored after verifying against reading progress,
 * and holding that here is what stops a browser whose claim was refused from
 * re-asking on every page load forever.
 */
export interface StoredEngagement {
  /** Distinct route paths seen. Capped; order is arrival order. */
  routes: string[];
  /** Highest tier the server has acknowledged storing. */
  reportedTier: number;
  /**
   * Highest tier this browser has ever ASKED the server about — which may sit
   * above `reportedTier` forever, when verification refused the claim. It is
   * what stops a refused candidate from re-asking on every navigation
   * (`src/lib/engagement/signals.ts`). Optional because blobs written before
   * it existed lack it; readers treat absence as `reportedTier`.
   */
  askedTier?: number;
  /**
   * When the server was last asked, epoch ms.
   *
   * Needed because two of the tiers — coming back after a gap, and anything
   * that happened in another tab — are things only the SERVER can see. Without
   * a periodic re-ask, a visitor who reads a chapter, leaves for a day, and
   * comes back to read nothing more would never trigger a call, and the return
   * would go unrecorded.
   */
  lastReportAt: number;
}

function isSyncMode(value: unknown): value is SyncMode {
  return value === "interpolated" || value === "hard";
}

function isFontSizePrefs(value: unknown): value is FontSizePrefs {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Partial<FontSizePrefs>;
  return (
    typeof v.retelling === "number" &&
    typeof v.original === "number" &&
    typeof v.linked === "boolean"
  );
}

function isTargetLang(value: unknown): value is TargetLang {
  // Mirrors `convex/model/language.ts`'s `targetLangValidator`: an open
  // string, not a literal union — any non-empty string is a valid request.
  return typeof value === "string" && value.length > 0;
}

function isSessionId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** Same bar as `isSessionId`: the strict shape check for an *inbound* lead id
 *  lives in `logging/attribution.ts`, which is what decides whether a value
 *  off the URL is storable in the first place. */
function isLeadId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** Shape only — the *content* checks (which sources we accept, how long a
 *  value may be) live in `logging/attribution.ts`, which is what decides
 *  whether a value off the URL is storable at all. */
function isStoredClickId(value: unknown): value is StoredClickId {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Partial<StoredClickId>;
  return (
    typeof c.source === "string" &&
    c.source.length > 0 &&
    typeof c.value === "string" &&
    c.value.length > 0 &&
    typeof c.at === "number" &&
    Number.isFinite(c.at)
  );
}

function isFounderNoticeSilencedUntil(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isPrivacyNoticeShownAt(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Shape only. The CAPS are enforced on write in `src/lib/engagement/`, so a
 *  hand-edited value with a million routes is read back as-is and then trimmed
 *  the next time it is written — rather than discarded, which would reset a
 *  visitor's `reportedTier` and make them re-report tiers already sent. */
function isStoredEngagement(value: unknown): value is StoredEngagement {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Partial<StoredEngagement>;
  return (
    Array.isArray(e.routes) &&
    e.routes.every((r: unknown) => typeof r === "string") &&
    typeof e.reportedTier === "number" &&
    Number.isFinite(e.reportedTier) &&
    // Absent on blobs written before it existed — rejecting those would reset
    // `reportedTier` and re-report value already sent (see `trim`'s comment).
    (e.askedTier === undefined ||
      (typeof e.askedTier === "number" && Number.isFinite(e.askedTier))) &&
    typeof e.lastReportAt === "number" &&
    Number.isFinite(e.lastReportAt)
  );
}

function isLocation(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const v = value as { blockId?: unknown; offset?: unknown };
  return typeof v.blockId === "string" && typeof v.offset === "number";
}

function isLocalBookmarks(value: unknown): value is LocalBookmark[] {
  if (!Array.isArray(value)) return false;
  return value.every((item: unknown) => {
    if (typeof item !== "object" || item === null) return false;
    const b = item as Partial<LocalBookmark>;
    return (
      (b.kind === "bookmark" || b.kind === "position") &&
      typeof b.bookSlug === "string" &&
      typeof b.chapterNumber === "number" &&
      isLocation(b.location)
    );
  });
}

function isLocalReadMarks(value: unknown): value is LocalReadMark[] {
  if (!Array.isArray(value)) return false;
  return value.every((item: unknown) => {
    if (typeof item !== "object" || item === null) return false;
    const m = item as Partial<LocalReadMark>;
    return (
      typeof m.bookSlug === "string" &&
      typeof m.chapterNumber === "number" &&
      typeof m.markedAt === "number"
    );
  });
}

/**
 * Per-key runtime validators for stored (untrusted) JSON. Every key currently
 * has one; a key without an entry is passed through unvalidated.
 */
const VALIDATORS: {
  [K in PrefKey]?: (value: unknown) => value is PrefValueMap[K];
} = {
  targetLang: isTargetLang,
  syncMode: isSyncMode,
  fontSize: isFontSizePrefs,
  sessionId: isSessionId,
  leadId: isLeadId,
  clickId: isStoredClickId,
  bookmarks: isLocalBookmarks,
  readMarks: isLocalReadMarks,
  founderNoticeSilencedUntil: isFounderNoticeSilencedUntil,
  engagement: isStoredEngagement,
  privacyNoticeShownAt: isPrivacyNoticeShownAt,
};

/**
 * The active `localStorage`, or `null` when it is unavailable (server-side
 * render, a non-DOM test runtime, or a browser that throws on access because
 * storage is disabled). Read dynamically on every call so tests can install a
 * mock.
 */
function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Read a preference, returning `fallback` when unset, unparseable, or failing
 * the key's validator (guards against stale/tampered storage — e.g. a
 * corrupted `targetLang` value flowing out typed as `TargetLang` unchecked).
 *
 * `null` is accepted as a fallback for keys whose ABSENCE is meaningful (there
 * is no neutral `StoredClickId`), via its own overload rather than by widening
 * the generic — widening would let the return type be inferred from the
 * fallback again, which is exactly the hole `PrefValueMap` closed.
 */
export function getPref<K extends PrefKey>(
  key: K,
  fallback: null,
): PrefValueMap[K] | null;
export function getPref<K extends PrefKey>(
  key: K,
  fallback: PrefValueMap[K],
): PrefValueMap[K];
export function getPref<K extends PrefKey>(
  key: K,
  fallback: PrefValueMap[K] | null,
): PrefValueMap[K] | null {
  const store = storage();
  if (!store) return fallback;
  const raw = store.getItem(PREFIX + key);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    const validate = VALIDATORS[key];
    if (validate && !validate(parsed)) return fallback;
    return parsed as PrefValueMap[K];
  } catch {
    return fallback;
  }
}

/** Write a preference. No-ops when storage is unavailable or write fails. */
export function setPref<K extends PrefKey>(
  key: K,
  value: PrefValueMap[K],
): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode write failures — prefs are best-effort.
  }
}

/** Remove a preference. */
export function removePref(key: PrefKey): void {
  const store = storage();
  if (!store) return;
  store.removeItem(PREFIX + key);
}
